import { useEffect, useId, useState } from 'react';
import { Redo2, Undo2, X } from 'lucide-react';
import { CHORD_CATEGORIES, CHORD_TYPES, type ChordTypeId } from '@/music/chords';
import { chartChordSymbol, IREAL_QUALITY_BY_TYPE } from '@/music/chart';
import { prettyQuality } from '@/music/ireal/chordParser';
import type { IRealChord } from '@/music/ireal/types';
import { accidentalForKey, CHROMATIC_FLAT, parseKey, pitchClassOf } from '@/music/notes';
import type { ChordPatch } from '@/state/store';
import { NoteSelect } from './pickers';
import './ScoreChordEditor.css';

const WRITTEN_TYPE = 'written';
type DraftType = ChordTypeId | typeof WRITTEN_TYPE;

interface ChordDraft {
  root: string;
  type: DraftType;
  bass: string;
  parentheses: NonNullable<IRealChord['parentheses']> | '';
}

function savedType(chordType: ChordTypeId | null, quality: string, noChord?: boolean): DraftType {
  return !noChord && chordType && IREAL_QUALITY_BY_TYPE[chordType] === quality
    ? chordType
    : WRITTEN_TYPE;
}

/** NoteSelect supplies canonical sharp names; authored changes use the score's key. */
function spellForKey(note: string, key: string | undefined): string {
  if (!note || accidentalForKey(parseKey(key)) !== 'flat') return note;
  return CHROMATIC_FLAT[pitchClassOf(note)];
}

/** Correct one written chord without exposing structural bar editing. */
export function ScoreChordEditor({
  chord,
  barNumber,
  musicKey,
  canUndo = false,
  canRedo = false,
  onUpdate,
  onUndo,
  onRedo,
  onClose,
}: {
  chord: IRealChord;
  barNumber: number;
  musicKey?: string;
  canUndo?: boolean;
  canRedo?: boolean;
  onUpdate: (patch: ChordPatch) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onClose: () => void;
}) {
  const { id, root, chordType, quality, bass, noChord, beats, parentheses } = chord;
  const [draft, setDraft] = useState<ChordDraft>(() => ({
    root,
    type: savedType(chordType, quality, noChord),
    bass: bass ?? '',
    parentheses: parentheses ?? '',
  }));
  // Projected chord objects can be recreated by unrelated score/UI updates.
  // Reset only when this chord's saved values or selection actually change.
  useEffect(() => {
    setDraft({
      root,
      type: savedType(chordType, quality, noChord),
      bass: bass ?? '',
      parentheses: parentheses ?? '',
    });
  }, [id, root, chordType, quality, bass, noChord, beats, parentheses]);
  const fieldId = useId();
  const written = savedType(chordType, quality, noChord) === WRITTEN_TYPE;
  const useWrittenType = draft.type === WRITTEN_TYPE;
  const draftQuality = draft.type === WRITTEN_TYPE ? quality : IREAL_QUALITY_BY_TYPE[draft.type];
  const typeChanged =
    !useWrittenType && (draft.type !== chordType || draftQuality !== quality || !!noChord);
  const stillNoChord = !!noChord && !typeChanged;

  const patch: ChordPatch = {};
  if (draft.root !== root) {
    patch.root = draft.root;
    // A root patch rebuilds the chord in the store. Carry the exact written
    // quality, including an empty major suffix, instead of adding extensions.
    patch.quality = draftQuality;
  }
  if (typeChanged && draft.type !== WRITTEN_TYPE) {
    patch.chordType = draft.type;
    patch.quality = draftQuality;
    if (noChord) patch.noChord = false;
  }
  if (draft.bass !== (bass ?? '')) patch.bass = draft.bass || undefined;
  if (draft.parentheses !== (parentheses ?? '')) patch.parentheses = draft.parentheses || undefined;
  const changed = Object.keys(patch).length > 0;
  const symbol = stillNoChord
    ? 'N.C.'
    : chartChordSymbol({
        root: draft.root,
        chordType: draft.type === WRITTEN_TYPE ? chordType : draft.type,
        quality: draftQuality,
        ...(draft.parentheses ? { parentheses: draft.parentheses } : {}),
        ...(draft.bass ? { bass: draft.bass } : {}),
      });

  return (
    <form
      className="score-chord-editor no-print"
      role="group"
      aria-label="Selected chord"
      data-testid="score-chord-editor"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          onClose();
          return;
        }
        if (event.altKey || (!event.metaKey && !event.ctrlKey)) return;
        const key = event.key.toLowerCase();
        const undo = key === 'z' && !event.shiftKey;
        const redo =
          (key === 'z' && event.shiftKey) || (key === 'y' && event.ctrlKey && !event.metaKey);
        if (!undo && !redo) return;
        event.preventDefault();
        event.stopPropagation();
        if (undo && canUndo) onUndo?.();
        if (redo && canRedo) onRedo?.();
      }}
      onSubmit={(event) => {
        event.preventDefault();
        if (changed && !stillNoChord) onUpdate(patch);
      }}
    >
      <div className="score-chord-fields">
        <label htmlFor={`${fieldId}-root`}>
          <span>Root</span>
          <NoteSelect
            id={`${fieldId}-root`}
            aria-label="Chord root"
            value={draft.root}
            includeBlank={!draft.root}
            disabled={stillNoChord}
            onChange={(value) =>
              setDraft((current) => ({ ...current, root: spellForKey(value, musicKey) }))
            }
          />
        </label>
        <label htmlFor={`${fieldId}-type`}>
          <span>Type</span>
          <select
            id={`${fieldId}-type`}
            aria-label="Chord type"
            value={draft.type}
            onChange={(event) => {
              const type = event.target.value as DraftType;
              setDraft((current) => ({
                ...current,
                type,
                root: noChord ? (type === WRITTEN_TYPE ? root : current.root || 'C') : current.root,
                bass: noChord && type === WRITTEN_TYPE ? (bass ?? '') : current.bass,
              }));
            }}
          >
            {written && (
              <option value={WRITTEN_TYPE}>
                {noChord
                  ? 'No chord (N.C.)'
                  : `${quality ? prettyQuality(quality) : 'Major'} (as written)`}
              </option>
            )}
            {CHORD_CATEGORIES.map((category) => (
              <optgroup key={category.category} label={category.label}>
                {category.types.map((type) => (
                  <option key={type} value={type}>
                    {CHORD_TYPES[type].name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
        <label htmlFor={`${fieldId}-bass`}>
          <span>Slash bass</span>
          <NoteSelect
            id={`${fieldId}-bass`}
            aria-label="Slash bass"
            value={draft.bass}
            includeBlank
            blankLabel="None"
            disabled={stillNoChord}
            onChange={(value) =>
              setDraft((current) => ({ ...current, bass: spellForKey(value, musicKey) }))
            }
          />
        </label>
        <label htmlFor={`${fieldId}-parentheses`}>
          <span>Brackets</span>
          <select
            id={`${fieldId}-parentheses`}
            aria-label="Chord parentheses"
            title="Parentheses around optional chords"
            value={draft.parentheses}
            disabled={stillNoChord}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                parentheses: event.target.value as ChordDraft['parentheses'],
              }))
            }
          >
            <option value="">No brackets</option>
            <option value="both">(Chord)</option>
            <option value="start">(Start group</option>
            <option value="stop">End group)</option>
          </select>
        </label>
      </div>
      <div className="score-chord-actions">
        <span className="score-chord-summary" aria-live="polite">
          <span>Bar {barNumber}</span>
          <strong title={symbol}>{symbol}</strong>
        </span>
        <button
          className="btn btn-sm btn-primary"
          type="submit"
          data-testid="score-chord-update"
          disabled={!changed || stillNoChord}
        >
          Update
        </button>
        <button
          className="btn btn-sm"
          type="button"
          disabled={!canUndo || !onUndo}
          onClick={onUndo}
        >
          <Undo2 size={16} aria-hidden="true" /> Undo
        </button>
        {onRedo && (
          <button
            className="btn btn-sm score-chord-icon-button"
            type="button"
            aria-label="Redo chord edit"
            title="Redo"
            disabled={!canRedo}
            onClick={onRedo}
          >
            <Redo2 size={18} aria-hidden="true" />
          </button>
        )}
        <button
          className="close-circle"
          type="button"
          aria-label="Finish editing chord"
          title="Close chord editor"
          onClick={onClose}
        >
          <X size={20} aria-hidden="true" />
        </button>
      </div>
    </form>
  );
}
